import type { Cell, CellValue, Workbook, Worksheet } from "exceljs";

import type { ImportCell } from "../dataImport/types";
import { toDayString } from "./cells";
import type { ExportLookups, SheetCell, SheetColumn } from "./types";

// Brand navy of the header row, white text.
const HEADER_FILL = "FF0C132B";
const HEADER_FONT = "FFFFFFFF";
const EXAMPLE_FONT = "FF6B7280";
/** Rows of the template that carry the dropdowns and the input checks. */
const TEMPLATE_ROWS = 500;
const XLSX_TYPE =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

/** ExcelJS is big: it is only downloaded when a file is exported or imported. */
const loadExcelJs = async () => (await import("exceljs")).default;

/** Texts of the template, in the user's language. */
export interface TemplateTexts {
  instructionsSheet: string;
  listsSheet: string;
  title: string;
  steps: string[];
  tableHeaders: [string, string, string, string];
  required: string;
  optional: string;
  kinds: Record<SheetColumn<unknown>["kind"], string>;
  listPrompt: string;
  choicesPrompt: string;
  ratingError: string;
  listError: string;
  numberError: string;
  dateError: string;
}

/** Writes the records as a formatted sheet and downloads it. */
export const downloadExport = async <RecordType>({
  fileName,
  ...options
}: Parameters<typeof buildExportWorkbook<RecordType>>[0] & {
  fileName: string;
}) => saveWorkbook(await buildExportWorkbook(options), fileName);

/** Workbook of an export: one formatted sheet, a row per record. */
export const buildExportWorkbook = async <RecordType>({
  sheetName,
  columns,
  records,
  lookups,
  currency,
}: {
  sheetName: string;
  columns: SheetColumn<RecordType>[];
  records: RecordType[];
  lookups: ExportLookups;
  currency: string;
}) => {
  const ExcelJS = await loadExcelJs();
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Opticercle";
  const sheet = addDataSheet(workbook, sheetName, columns, currency);
  records.forEach((record) => {
    sheet.addRow(
      columns.map((column) => toCellValue(column.get(record, lookups))),
    );
  });
  sheet.autoFilter = {
    from: { row: 1, column: 1 },
    to: { row: 1, column: columns.length },
  };
  return workbook;
};

/**
 * Import template: the importable columns with a sample row, dropdowns and
 * input checks on the first rows, a sheet listing every allowed value, and an
 * instructions sheet describing each column.
 */
export const downloadTemplate = async <RecordType>({
  fileName,
  ...options
}: Parameters<typeof buildTemplateWorkbook<RecordType>>[0] & {
  fileName: string;
}) => saveWorkbook(await buildTemplateWorkbook(options), fileName);

/** Workbook of the import template (see downloadTemplate). */
export const buildTemplateWorkbook = async <RecordType>({
  sheetName,
  columns,
  currency,
  texts,
}: {
  sheetName: string;
  columns: SheetColumn<RecordType>[];
  currency: string;
  texts: TemplateTexts;
}) => {
  const ExcelJS = await loadExcelJs();
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Opticercle";
  const importable = columns.filter((column) => !column.exportOnly);
  const sheet = addDataSheet(
    workbook,
    sheetName,
    importable.map((column) => ({
      ...column,
      header: column.required ? `${column.header} *` : column.header,
    })),
    currency,
  );
  const example = sheet.addRow(
    importable.map((column) => toCellValue(column.example)),
  );
  example.font = { italic: true, color: { argb: EXAMPLE_FONT } };

  const lists = workbook.addWorksheet(texts.listsSheet);
  let listColumn = 0;
  importable.forEach((column, index) => {
    const validation = validationOf(column, texts, () => {
      listColumn += 1;
      return writeList(lists, listColumn, column, texts.listsSheet);
    });
    if (!validation) return;
    for (let row = 2; row <= TEMPLATE_ROWS; row++) {
      sheet.getCell(row, index + 1).dataValidation = validation;
    }
  });

  addInstructionsSheet(workbook, importable, texts);
  // Open on the data sheet, the instructions right after it.
  workbook.views = [
    {
      x: 0,
      y: 0,
      width: 20000,
      height: 12000,
      firstSheet: 0,
      activeTab: 0,
      visibility: "visible",
    },
  ];
  return workbook;
};

/**
 * Rows of the first sheet of an Excel file, keyed by header. Dates become
 * "YYYY-MM-DD", formulas their result, links and rich text their text.
 */
export const readSpreadsheet = async (
  file: File,
): Promise<Record<string, ImportCell>[]> => {
  const ExcelJS = await loadExcelJs();
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(await file.arrayBuffer());
  const sheet = workbook.worksheets[0];
  if (!sheet) return [];
  const headers: string[] = [];
  sheet.getRow(1).eachCell((cell, column) => {
    headers[column] = String(readCell(cell) ?? "").trim();
  });
  const rows: Record<string, ImportCell>[] = [];
  sheet.eachRow((row, rowNumber) => {
    if (rowNumber === 1) return;
    const values: Record<string, ImportCell> = {};
    let isEmpty = true;
    headers.forEach((header, column) => {
      if (!header) return;
      const value = readCell(row.getCell(column));
      values[header] = value;
      if (value !== null && value !== "") isEmpty = false;
    });
    if (!isEmpty) rows.push(values);
  });
  return rows;
};

const addDataSheet = <RecordType>(
  workbook: Workbook,
  name: string,
  columns: SheetColumn<RecordType>[],
  currency: string,
) => {
  const sheet = workbook.addWorksheet(name, {
    views: [{ state: "frozen", ySplit: 1 }],
  });
  sheet.columns = columns.map((column) => ({
    header: column.header,
    width: column.width ?? Math.max(16, column.header.length + 4),
    style: {
      numFmt: numberFormatOf(column, currency),
      alignment: alignmentOf(column),
    },
  }));
  const header = sheet.getRow(1);
  header.height = 22;
  header.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: HEADER_FONT } };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: HEADER_FILL },
    };
    cell.alignment = { vertical: "middle" };
  });
  return sheet;
};

const numberFormatOf = <RecordType>(
  column: SheetColumn<RecordType>,
  currency: string,
): string | undefined => {
  switch (column.kind) {
    case "currency":
      return `#,##0 "${currency}"`;
    case "number":
      return "#,##0.##";
    case "date":
      return "dd.mm.yyyy";
    case "datetime":
      return "dd.mm.yyyy hh:mm";
    case "phone":
      // Text: keeps leading zeros and "+41" as typed.
      return "@";
    default:
      return undefined;
  }
};

const alignmentOf = <RecordType>(column: SheetColumn<RecordType>) =>
  column.kind === "long_text"
    ? ({ wrapText: true, vertical: "top" } as const)
    : ({ vertical: "top" } as const);

/** Allowed values of a dropdown column, in their own column of the lists sheet. */
const writeList = <RecordType>(
  lists: Worksheet,
  listColumn: number,
  column: SheetColumn<RecordType>,
  listsSheetName: string,
) => {
  const options = column.options ?? [];
  const header = lists.getCell(1, listColumn);
  header.value = column.header;
  header.font = { bold: true, color: { argb: HEADER_FONT } };
  header.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: HEADER_FILL },
  };
  lists.getColumn(listColumn).width = Math.max(18, column.header.length + 4);
  options.forEach((option, index) => {
    lists.getCell(index + 2, listColumn).value = option.label;
  });
  const letter = lists.getColumn(listColumn).letter;
  return `'${listsSheetName}'!$${letter}$2:$${letter}$${Math.max(2, options.length + 1)}`;
};

const validationOf = <RecordType>(
  column: SheetColumn<RecordType>,
  texts: TemplateTexts,
  listRange: () => string,
): Cell["dataValidation"] | undefined => {
  const prompt = column.hint
    ? {
        showInputMessage: true,
        promptTitle: column.header,
        prompt: column.hint,
      }
    : {};
  switch (column.kind) {
    case "choice":
    case "boolean":
      return column.options?.length
        ? {
            type: "list",
            allowBlank: true,
            formulae: [listRange()],
            showErrorMessage: true,
            errorStyle: "stop",
            errorTitle: column.header,
            error: texts.listError,
            showInputMessage: true,
            promptTitle: column.header,
            prompt: column.hint ?? texts.listPrompt,
          }
        : undefined;
    case "choices":
      // Several values separated by commas: the list helps, but must not refuse them.
      return column.options?.length
        ? {
            type: "list",
            allowBlank: true,
            formulae: [listRange()],
            showErrorMessage: false,
            showInputMessage: true,
            promptTitle: column.header,
            prompt: texts.choicesPrompt,
          }
        : undefined;
    case "rating":
      return {
        type: "whole",
        operator: "between",
        allowBlank: true,
        formulae: [1, 5],
        showErrorMessage: true,
        errorStyle: "stop",
        errorTitle: column.header,
        error: texts.ratingError,
        ...prompt,
      };
    case "number":
    case "currency":
      return {
        type: "decimal",
        operator: "greaterThanOrEqual",
        allowBlank: true,
        formulae: [0],
        showErrorMessage: true,
        errorStyle: "stop",
        errorTitle: column.header,
        error: texts.numberError,
        ...prompt,
      };
    case "date":
      return {
        type: "date",
        operator: "greaterThan",
        allowBlank: true,
        formulae: [new Date(Date.UTC(1900, 0, 1))],
        showErrorMessage: true,
        errorStyle: "stop",
        errorTitle: column.header,
        error: texts.dateError,
        ...prompt,
      };
    default:
      return column.hint
        ? { type: "custom", allowBlank: true, formulae: ["TRUE"], ...prompt }
        : undefined;
  }
};

const addInstructionsSheet = <RecordType>(
  workbook: Workbook,
  columns: SheetColumn<RecordType>[],
  texts: TemplateTexts,
) => {
  const sheet = workbook.addWorksheet(texts.instructionsSheet);
  sheet.getColumn(1).width = 34;
  sheet.getColumn(2).width = 14;
  sheet.getColumn(3).width = 70;
  sheet.getColumn(4).width = 30;
  const title = sheet.addRow([texts.title]);
  title.font = { bold: true, size: 14 };
  texts.steps.forEach((step) => sheet.addRow([step]));
  sheet.addRow([]);
  const header = sheet.addRow(texts.tableHeaders);
  header.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: HEADER_FONT } };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: HEADER_FILL },
    };
  });
  columns.forEach((column) => {
    const values = column.options?.length
      ? column.options.map((option) => option.label).join(" · ")
      : texts.kinds[column.kind];
    const row = sheet.addRow([
      column.header,
      column.required ? texts.required : texts.optional,
      column.hint ? `${values}\n${column.hint}` : values,
      toCellValue(column.example) ?? "",
    ]);
    row.alignment = { wrapText: true, vertical: "top" };
  });
};

const toCellValue = (value: SheetCell): CellValue => {
  if (value === undefined || value === null) return null;
  if (typeof value === "string") {
    const text = value.trim();
    return text === "" ? null : text;
  }
  return value;
};

const readCell = (cell: Cell): ImportCell => {
  const value = cell.value;
  if (value === null || value === undefined) return null;
  if (value instanceof Date) return toDayString(value);
  if (typeof value === "object") {
    if ("result" in value) return normalize(value.result);
    if ("richText" in value)
      return value.richText.map((part) => part.text).join("");
    if ("text" in value) return String(value.text);
    if ("error" in value) return null;
    return null;
  }
  return value as ImportCell;
};

const normalize = (value: unknown): ImportCell => {
  if (value instanceof Date) return toDayString(value);
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return value;
  }
  return null;
};

const saveWorkbook = async (workbook: Workbook, fileName: string) => {
  const buffer = await workbook.xlsx.writeBuffer();
  const url = URL.createObjectURL(new Blob([buffer], { type: XLSX_TYPE }));
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  // Revoked once the browser has started the download.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
};
